import { useRef, useState } from 'react';
import { Button, Center, Group, Loader, Modal, Stack, Switch, TextInput } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import { useFlags } from 'src/feature-flags/FlagsHook';
import useSaveTenantSkill from './hooks/useSaveTenantSkill';
import SkillFilesSection from './SkillFilesSection';
import type { SiestaSkill } from './types';

const NAME_PATTERN = /^[a-z][a-z0-9_]{2,49}$/;

type SkillEditorModalProps = {
  opened: boolean;
  skill: SiestaSkill | null;
  // Editing a skill whose fresh copy has not arrived yet. A null skill on its own means create.
  loading?: boolean;
  onClose: () => void;
};

export default function SkillEditorModal({
  opened,
  skill,
  loading = false,
  onClose,
}: SkillEditorModalProps) {
  let title = 'Create a skill';
  if (loading) title = 'Edit skill';
  else if (skill) title = `Edit ${skill.name}`;

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="xl" centered>
      {opened && loading && (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      )}
      {opened && !loading && (
        <SkillEditorForm key={skill?.id ?? 'new'} skill={skill} onClose={onClose} />
      )}
    </Modal>
  );
}

function SkillEditorForm({ skill, onClose }: { skill: SiestaSkill | null; onClose: () => void }) {
  const { flagEnabledCustomSkillFiles, flagEnabledSiestaSuitabilityReportProposals } = useFlags();
  const [name, setName] = useState(skill?.name ?? '');
  const [description, setDescription] = useState(skill?.description ?? '');
  const [isEnabled, setIsEnabled] = useState(skill?.isEnabled ?? true);
  const [companyFilesVisible, setCompanyFilesVisible] = useState(
    skill?.companyFilesVisible ?? false,
  );
  const [canGenerateSuitabilityReports, setCanGenerateSuitabilityReports] = useState(
    skill?.canGenerateSuitabilityReports ?? false,
  );
  const [hasMarkdownContent, setHasMarkdownContent] = useState(
    (skill?.markdown ?? '').trim().length > 0,
  );
  const markdownRef = useRef(skill?.markdown ?? '');

  const { mutateAsync: saveSkill, isPending } = useSaveTenantSkill();

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isNameValid = NAME_PATTERN.test(trimmedName);
  const canSave = isNameValid && trimmedDescription.length > 0 && hasMarkdownContent;

  const nameError =
    trimmedName.length > 0 && !isNameValid
      ? 'Use snake_case: lowercase letters, digits and underscores, starting with a letter (3 to 50 characters).'
      : null;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await saveSkill({
        id: skill?.id,
        name: trimmedName,
        description: trimmedDescription,
        markdown: markdownRef.current,
        companyFilesVisible,
        canGenerateSuitabilityReports,
        isEnabled,
      });
      onClose();
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while saving the skill.';
      notifyAndLogError('Failed to save skill', detail, error);
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Name"
        description="Identifier Siesta uses to activate the skill (snake_case, e.g. quarterly_review_pack)"
        placeholder="quarterly_review_pack"
        value={name}
        onChange={e => setName(e.currentTarget.value)}
        error={nameError}
        required
      />
      <TextInput
        label="Description"
        description="Short summary Siesta reads to decide when to use the skill"
        placeholder="Build a branded quarterly review pack for a client"
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
        required
      />
      <Switch
        label="Does Siesta need to see your company documents when using this skill?"
        checked={companyFilesVisible}
        onChange={e => setCompanyFilesVisible(e.currentTarget.checked)}
      />
      {flagEnabledSiestaSuitabilityReportProposals === true && (
        <Switch
          label="Allow Siesta to generate suitability reports"
          description="Siesta can prepare a suitability report from the documents it drafts. The adviser checks it and generates it in the report flow."
          checked={canGenerateSuitabilityReports}
          onChange={e => setCanGenerateSuitabilityReports(e.currentTarget.checked)}
        />
      )}
      <Switch
        label="Enabled"
        description="Disabled skills are hidden from chat without being deleted"
        checked={isEnabled}
        onChange={e => setIsEnabled(e.currentTarget.checked)}
      />
      <MarkdownEditor
        defaultValue={markdownRef.current}
        onChange={value => {
          markdownRef.current = value;
          setHasMarkdownContent(value.trim().length > 0);
        }}
        label="Skill definition"
        rootClass="compact"
      />
      {skill && flagEnabledCustomSkillFiles === true && <SkillFilesSection skillId={skill.id} />}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={isPending} disabled={!canSave}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
